import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    Signal,
    SimpleChanges,
    computed,
    signal,
} from '@angular/core';
import { cloneDeep, memoize } from 'lodash';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PhaseModel, ProjectModel, ProjectStatus } from '../../../project/project.models';
import { DailyTimeEntryModel, TimesheetModel } from '../../timesheet.models';
import { GenericCardComponent } from 'src/app/shared/components/generic-card/generic-card.component';
import { SharedModule } from 'src/app/shared/shared.module';
import {
    ButtonColors,
    ButtonModule,
    CheckboxModule,
    NumberfieldModule,
} from '@ed---interne/ng-uui-components';
import { SkeletonListComponent } from 'src/app/shared/components/skeleton-list/skeleton-list.component';
import { RouterModule } from '@angular/router';

/* ---- Modèles ---- */

/* ---- UI Row ---- */
type Row = {
    projectId: string;
    phaseId: string;
    createdAt: Date;
};

function almostEqual(a: number, b: number, eps = 0.001) {
    return Math.abs(a - b) <= eps;
}

const UNSET = 'UNSET';
@Component({
    selector: 'timesheet-grid-card',
    standalone: true,
    imports: [
        CommonModule,
        SharedModule,
        FormsModule,
        GenericCardComponent,
        NumberfieldModule,
        RouterModule,
        CheckboxModule,
        ButtonModule,
        SkeletonListComponent,
    ],
    templateUrl: './timesheet-grid-card.component.html',
    styleUrls: ['./timesheet-grid-card.component.scss'],
})
export class TimesheetGridCardComponent implements OnChanges {
    ButtonColors = ButtonColors;
    UNSET = UNSET;
    /** Header GenericCard */
    @Input({ required: true }) isLoading = false; // <- uniquement passé au GenericCard

    /** Données */
    public _projects = signal<ProjectModel[]>([]);
    public projectsLinks = signal<{ [key: string]: { label: string; link: string } }>({});
    public phaseLinks = signal<{ [key: string]: { label: string; link: string } }>({});

    @Input({ required: true }) set projects(projects: ProjectModel[]) {
        this._projects.set(projects);
        const phaseLinks: { [key: string]: { label: string; link: string } } = {};
        const projectsLinks: { [key: string]: { label: string; link: string } } = {};
        projects.map((project) => {
            projectsLinks[project.id] = {
                label: `${project.code} - ${project.label}`,
                link: `/main/projects/${project.code}/`,
            };
            project.phases?.map(
                (phase) =>
                    (phaseLinks[phase.id] = {
                        label: `${phase.code} - ${phase.label}`,
                        link: `/main/projects/${project.code}/phase/${phase.code}`,
                    }),
            );
        });
        this.phaseLinks.set(phaseLinks);
        this.projectsLinks.set(projectsLinks);
    }
    @Input({ required: true }) timesheets: TimesheetModel[] = []; // >5 => on tronque
    @Input() canEdit = true;

    /** Sorties */
    @Output() onSearchProject = new EventEmitter<{
        phaseId?: string;
        projectId?: string;
        q?: string;
    }>(); // enrichir projects
    @Output() save = new EventEmitter<TimesheetModel[]>(); // sauvegarder
    @Output() onEdit = new EventEmitter<boolean>(); // prévenir navigation

    /* ---- État ---- */

    // Toujours n’utiliser que les 5 premiers jours
    week5 = signal<TimesheetModel[]>([]);
    days = computed(() => this.week5().map((t) => new Date(t.date))); //Fix, not recreate with signals
    rows = computed(() =>
        this.week5()
            .reduce((acc: Row[], t: TimesheetModel) => {
                t.dailyTimeEntries.map((d) => {
                    const projectId = this.findProjectIdByPhase(d.phaseId);
                    if (!projectId) {
                        this.onSearchProject.emit({ phaseId: d.phaseId });
                    }
                    const row: Row = {
                        phaseId: d.phaseId || UNSET,
                        projectId: projectId || UNSET,
                        createdAt: d.createdAt,
                    };
                    const existing = acc.find((r) => r.phaseId === row.phaseId);
                    if (!existing) acc.push(row);
                    else {
                        existing.createdAt =
                            existing.createdAt < row.createdAt ? existing.createdAt : row.createdAt;
                    }
                });
                return acc;
            }, [])
            .sort((a, b) => (a.createdAt > b.createdAt ? 1 : 0)),
    );

    ngOnChanges(changes: SimpleChanges) {
        console.log('changes detected !');
        if (changes['timesheets']) {
            console.log('AND ITS TIMESHETS !');
            this.week5.set(
                cloneDeep(changes['timesheets'].currentValue)
                    .sort((a: TimesheetModel, b: TimesheetModel) => (a.date < b.date ? 1 : 0))
                    .slice(0, 5),
            );
        }
    }
    // zone d’ajout
    addSearch = signal<string>('');
    addSelectedProjectId = signal<string | null>(null);
    addSelectedPhaseId = signal<string | null>(null);

    private _editing = signal<boolean>(false);
    editing = computed(() => this._editing());

    findDailyEntry(
        date: Date,
        phaseId: string,
        timesheets?: TimesheetModel[],
    ): DailyTimeEntryModel | undefined {
        if (!timesheets) timesheets = this.week5();
        return timesheets
            .find((t) => t.date.getTime() === date.getTime())
            ?.dailyTimeEntries?.find((d) => d.phaseId === phaseId);
    }

    findDailyEntries(phaseId: string, timesheets?: TimesheetModel[]): DailyTimeEntryModel[] {
        if (!timesheets) timesheets = this.week5();
        const entries: DailyTimeEntryModel[] = [];
        for (const tm of timesheets) {
            const e = tm.dailyTimeEntries.find((x) => x.phaseId === phaseId);
            if (e) entries.push(e);
        }
        return entries;
    }

    /* ---- Helpers ---- */
    private findProjectIdByPhase(phaseId: string): string | null {
        for (const p of this._projects())
            if ((p?.phases ?? []).some((ph) => ph.id === phaseId)) return p.id;
        return null;
    }

    private markEditing() {
        if (!this._editing()) {
            this._editing.set(true);
            this.onEdit.emit(true);
        }
    }

    /* ---- Actions ligne ---- */
    onProjectChange(rowIndex: number, projectId: string | null) {
        if (!projectId) return;

        const row = this.rows()[rowIndex];
        this.markEditing();

        row.projectId = projectId;

        const projectSelected = this._projects().find((p) => p.id === projectId);
        // phase toujours valide ? Normalement non...
        const phase = projectSelected?.phases?.find((ph) => ph.id === row.phaseId);

        //Si une seule phase dans le nouveau projet, on l'assigne par défaut
        const defaultPhaseId =
            phase?.id ||
            (projectSelected?.phases?.length === 1 ? projectSelected.phases[0].id : UNSET);

        //Tentative de mettre la nouvelle phase
        this.onPhaseChange(rowIndex, defaultPhaseId);
    }

    onPhaseChange(rowIndex: number, phaseId: string) {
        const row = this.rows()[rowIndex];
        this.markEditing();
        const oldPhaseId = row.phaseId;

        row.phaseId = phaseId;

        //UpdateValues
        this.week5.update((timesheets) => {
            const entries = this.findDailyEntries(oldPhaseId, timesheets);
            for (const e of entries) {
                e.phaseId = row.phaseId;
            }
            return timesheets;
        });
    }

    findDailyEntryValue(dayIndex: number, rowIndex: number): number | null {
        const row = this.rows()[rowIndex];
        const day = this.days()[dayIndex];
        const entry = this.findDailyEntry(day, row.phaseId);
        if (entry) {
            return Math.floor(entry.percentOfDay * 100);
        }
        return 0;
    }

    onCellInput(rowIndex: number, dayIndex: number, raw: any) {
        if (!this.canEdit) return;

        const day = this.days()[dayIndex];
        const row = this.rows()[rowIndex];
        const phaseId = row.phaseId;

        this.markEditing();

        const n = raw === '' ? null : Number(raw) / 100;

        this.week5.update((timesheets) => {
            const entry = this.findDailyEntry(day, phaseId, timesheets);
            if (n === null) {
                //Remove entry
                for (const timesheet of timesheets) {
                    if (timesheet.date.getTime() === day.getTime()) {
                        timesheet.dailyTimeEntries = timesheet.dailyTimeEntries.filter(
                            (d) => d.phaseId !== entry?.phaseId,
                        );
                    }
                }
            } else if (entry?.phaseId) {
                //Maj Date
                entry.percentOfDay = n;
            } else if (!entry) {
                //Create entry
                for (const timesheet of timesheets) {
                    if (timesheet.date.getTime() === day.getTime()) {
                        timesheet.dailyTimeEntries = [
                            ...timesheet.dailyTimeEntries,
                            {
                                createdAt: new Date(),
                                updatedAt: new Date(),
                                phaseId,
                                percentOfDay: n,
                                date: day,
                            },
                        ];
                    }
                }
            }
            return timesheets;
        });
    }

    removeRow(rowIndex: number) {
        if (!this.canEdit) return;

        const row = this.rows()[rowIndex];
        this.markEditing();
        this.week5.update((timesheets) => {
            for (const tm of timesheets) {
                tm.dailyTimeEntries = tm.dailyTimeEntries.filter((e) => e.phaseId !== row.phaseId);
            }
            return timesheets;
        });
    }

    /* ---- Ajout d'une ligne ---- */
    onAddSearchChange(q: string) {
        if (!this.canEdit) return;
        this.addSearch.set(q);
        this.onSearchProject.emit({ q }); // enrichissement côté parent
    }
    onAddProjectChange(projectId: string | null) {
        if (!this.canEdit) return;
        this.addSelectedProjectId.set(projectId);
        const projectSelected = this._projects().find((p) => p.id === projectId);
        if (projectSelected?.phases?.length === 1) {
            this.onAddPhaseChange(projectSelected?.phases[0].id);
        } else {
            this.addSelectedPhaseId.set(null);
        }
    }

    onAddPhaseChange(phaseId: any | null) {
        if (!this.canEdit) return;
        if (!phaseId) return;
        const projectId = this.addSelectedProjectId();

        const existingRow = this.rows().find((r) => r.phaseId === phaseId);
        //We don't allow same row
        if (existingRow) return;

        if (projectId && phaseId) {
            this.week5.update((timesheets) => {
                const clone = timesheets.slice();
                const timesheet = { ...clone[0] };
                const newEntry: DailyTimeEntryModel = {
                    createdAt: new Date(),
                    updatedAt: new Date(),
                    date: timesheet.date,
                    percentOfDay: 0 / 100,
                    phaseId,
                };
                timesheet.dailyTimeEntries = [...timesheet.dailyTimeEntries, newEntry];
                clone[0] = timesheet;
                return clone;
            });
            // reset zone d’ajout
            this.addSelectedProjectId.set(null);
            this.addSelectedPhaseId.set(null);
            this.addSearch.set('');
        }
    }

    /* ---- Totaux & affichage ---- */
    projectSum(rowIndex: number): number {
        const row = this.rows()[rowIndex];
        const phaseId = row.phaseId;
        return this.week5().reduce(
            (total, timesheet) =>
                total +
                timesheet.dailyTimeEntries.reduce(
                    (totalEntry, entry) =>
                        totalEntry + (entry.phaseId === phaseId ? Number(entry.percentOfDay) : 0),
                    0,
                ),
            0,
        );
    }

    daysRemaing(projectId: string): number {
        const project = this._projects().find((p) => p.id === projectId);
        if (!project) return 0;
        const daysRemaing = Math.floor(project.daysRemaining || 0);
        //We need to add back this week already stored in database
        const thisWeekAlreadyIncluded = this.timesheets.reduce(
            (total, t) =>
                total +
                t.dailyTimeEntries.reduce((tot, d) => {
                    const phase = project.phases?.find((p) => p.id === d.phaseId);
                    if (phase) return tot + d.percentOfDay;
                    return tot;
                }, 0),
            0,
        );
        return daysRemaing + thisWeekAlreadyIncluded;
    }

    columnTotal(d: Date): number {
        const entries = this.week5().find(
            (t) => t.date.getTime() === d.getTime(),
        )?.dailyTimeEntries;
        if (!entries) return 0;
        return (
            entries.reduce((s: number, r: DailyTimeEntryModel) => {
                return s + (Number(r.percentOfDay) || 0);
            }, 0) * 100
        );
    }

    isDayOk(d: Date): boolean {
        return almostEqual(this.columnTotal(d), 100);
    }

    /* ---- Options pour <ed-select> ---- */
    // option {label,value} pour les projets
    projectOptions = computed(() =>
        (this._projects() ?? []).map((p) => ({
            label: `${p.code} — ${p.label}`,
            value: p.id,
            disabled: p.status === ProjectStatus.CLOSED,
        })),
    );

    filteredProjectOptions = computed(() =>
        this.projectOptions().filter((p) =>
            p.label.toLowerCase().includes(this.addSearch().toLowerCase()),
        ),
    );

    // options de phase par projet -> Map<projectId, Option[]>
    phaseOptionsByProject = computed(() => {
        const m = new Map<string, { label: string; value: string }[]>();
        for (const p of this._projects() ?? []) {
            const opts = (p.phases ?? []).map((ph) => ({
                label: `${ph.code} — ${ph.label} ${ph.researchCategory ? '(R&D)' : ''}`,
                value: ph.id,
                disabled: this.rows().find((r) => r.phaseId === ph.id),
            }));
            m.set(p.id, opts);
        }
        return m;
    });

    isDirty = () => {
        return this.week5().some((timesheet: TimesheetModel) => {
            return (
                //If on day is > 100
                timesheet.dailyTimeEntries.reduce((tot, d) => tot + d.percentOfDay, 0) > 1 || //If phase is missing
                timesheet.dailyTimeEntries.some((d) => !d.phaseId || d.phaseId === UNSET, 0)
            );
        });
    };

    /* ---- Sauvegarde ---- */
    onSaveClick() {
        if (!this._editing() || this.isDirty()) return;

        const cleaned = this.week5().map((ts) => ({
            ...ts,
            dailyTimeEntries: ts.dailyTimeEntries.filter(
                (d) => d.percentOfDay > 0 && d.phaseId !== UNSET,
            ),
        }));
        this.save.emit(cleaned); // on renvoie les 5 jours utilisés
        this._editing.set(false);
        this.onEdit.emit(false);
    }
}
